import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../shared/api/api.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { UserProfile } from '../../auth/components/UserProfile.js';
import { ScanActivityChart } from './ScanActivityChart.js';
import { BookRegistrationTrendCard } from './BookRegistrationTrendCard.js';
import { ExportCsvModal } from './ExportCsvModal.js';
import { ExportPdfModal } from './ExportPdfModal.js';
import {
  DashboardHeader,
  RoleScopingNotice,
  MetricCardsGrid,
  AdminQuickActionsCard,
  StatusDistributionCard,
  CategoryBreakdownCard,
  TopScannedLeaderboard,
} from './dashboard-view/index.js';

export const DashboardView: React.FC = () => {
  const { user } = useAuthStore();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportPdfModalOpen, setIsExportPdfModalOpen] = useState(false);

  const { data: stats, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['stats', user?.id, user?.role],
    queryFn: () => api.getDashboardStats(),
    refetchInterval: 10000, // Real-time 10s auto-refresh
  });

  if (isLoading && !stats) {
    return (
      <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 shadow-2xs">
        <div className="inline-block w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-xs text-slate-500">Memuat statistik real-time UIBN...</p>
      </div>
    );
  }

  const summary = stats?.summary || {
    totalBooks: 0,
    totalApproved: 0,
    totalPending: 0,
    totalDrafts: 0,
    totalArchived: 0,
    totalScans: 0,
    totalUsers: 0,
  };

  const statusCounts: Record<string, number> = stats?.statusCounts || {
    disetujui: 0,
    diajukan: 0,
    ditolak: 0,
    draft: 0,
    diarsipkan: 0,
  };
  const categories = stats?.categoryBreakdown || [];
  const trends = stats?.scanTrends || [];
  const topScanned = stats?.topScanned || [];

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <DashboardHeader
        isFetching={isFetching}
        onOpenExportPdfModal={() => setIsExportPdfModalOpen(true)}
        onOpenExportCsvModal={() => setIsExportModalOpen(true)}
        onRefetch={() => refetch()}
      />

      {/* Role Scoping Notice */}
      <RoleScopingNotice
        user={user}
        onOpenProfileModal={() => setIsProfileModalOpen(true)}
      />

      {/* 4 Primary Metric Cards */}
      <MetricCardsGrid summary={summary} rolesCount={stats?.rolesCount} />

      {/* 30-Day Registration Trend (Admin Exclusive Visual Card) */}
      {user?.role === 'admin' && (
        <BookRegistrationTrendCard trends={stats?.bookRegistrationTrends} />
      )}

      {/* Charts / Breakdown Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Admin Quick Actions */}
        {user?.role === 'admin' && <AdminQuickActionsCard />}

        {/* 7-day Scan Trends (Responsive Visual Bar Chart) */}
        <div className={user?.role === 'admin' ? 'lg:col-span-1' : 'lg:col-span-2'}>
          <ScanActivityChart trends={trends} totalScans={summary.totalScans} />
        </div>

        {/* Status Distribution */}
        <StatusDistributionCard
          totalBooks={summary.totalBooks}
          statusCounts={statusCounts}
        />
      </div>

      {/* Categories Breakdown & Top Scanned */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <CategoryBreakdownCard categories={categories} />
        <TopScannedLeaderboard topScanned={topScanned} />
      </div>

      {/* User Profile Modal */}
      <UserProfile
        isModal={true}
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      {/* Export CSV Modal with Date Range Filters */}
      <ExportCsvModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      />

      {/* Export PDF Modal Report Generator */}
      <ExportPdfModal
        isOpen={isExportPdfModalOpen}
        onClose={() => setIsExportPdfModalOpen(false)}
      />
    </div>
  );
};
